<script>
import { mapActions, mapState } from 'vuex';
import {
  ReloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import bem from '@/utils/bem';
import PageHeader from '@/components/ux/pageHeader/page-header.vue';

const b = bem('frontend-errors');

export default {
  components: {
    PageHeader,
    ReloadOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    PlayCircleOutlined,
  },
  data() {
    return {
      title: 'Frontend Errors',
      filters: {
        period: '7d',
        dateRange: [],
        errorType: null,
        isResolved: null,
        search: '',
      },
      pagination: {
        page: 1,
        limit: 20,
      },
      selectedRowKeys: [],
      errorDetailVisible: false,
      selectedError: null,
      periodOptions: [
        { value: 'today', label: 'Today' },
        { value: 'yesterday', label: 'Yesterday' },
        { value: '7d', label: 'Last 7 days' },
        { value: '30d', label: 'Last 30 days' },
        { value: 'custom', label: 'Custom' },
      ],
      errorTypeOptions: [
        { value: null, label: 'All Types' },
        { value: 'TypeError', label: 'TypeError' },
        { value: 'ReferenceError', label: 'ReferenceError' },
        { value: 'SyntaxError', label: 'SyntaxError' },
        { value: 'Error', label: 'Error' },
        { value: 'UnhandledRejection', label: 'Unhandled Rejection' },
        { value: 'ConsoleError', label: 'Console Error' },
      ],
      resolvedOptions: [
        { value: null, label: 'All Status' },
        { value: false, label: 'Unresolved' },
        { value: true, label: 'Resolved' },
      ],
      columns: [
        {
          title: 'Error',
          key: 'error',
          ellipsis: true,
        },
        {
          title: 'Page',
          dataIndex: 'pagePath',
          key: 'pagePath',
          width: 200,
          ellipsis: true,
        },
        {
          title: 'Browser',
          dataIndex: 'browser',
          key: 'browser',
          width: 120,
        },
        {
          title: 'Occurred',
          dataIndex: 'occurredAt',
          key: 'occurredAt',
          width: 160,
        },
        {
          title: 'Status',
          dataIndex: 'isResolved',
          key: 'isResolved',
          width: 100,
        },
        {
          title: 'Actions',
          key: 'actions',
          width: 150,
          fixed: 'right',
        },
      ],
    };
  },
  computed: {
    ...mapState({
      errors: (state) => state.analytics.frontendErrors,
      errorsTotal: (state) => state.analytics.frontendErrorsTotal,
      errorsLoading: (state) => state.analytics.frontendErrorsLoading,
      errorDetail: (state) => state.analytics.frontendErrorDetail,
      errorDetailLoading: (state) => state.analytics.frontendErrorDetailLoading,
      resolvingErrors: (state) => state.analytics.resolvingFrontendErrors,
    }),
    filterParams() {
      const params = {
        period: this.filters.period,
        page: this.pagination.page,
        limit: this.pagination.limit,
      };
      if (this.filters.period === 'custom' && this.filters.dateRange.length === 2) {
        params.startDate = this.filters.dateRange[0].toISOString();
        params.endDate = this.filters.dateRange[1].toISOString();
      }
      if (this.filters.errorType) {
        params.errorType = this.filters.errorType;
      }
      if (this.filters.isResolved !== null) {
        params.isResolved = this.filters.isResolved;
      }
      if (this.filters.search) {
        params.search = this.filters.search;
      }
      return params;
    },
    paginationConfig() {
      return {
        current: this.pagination.page,
        pageSize: this.pagination.limit,
        total: this.errorsTotal,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} errors`,
      };
    },
    rowSelection() {
      return {
        selectedRowKeys: this.selectedRowKeys,
        onChange: (keys) => {
          this.selectedRowKeys = keys;
        },
      };
    },
    hasSelected() {
      return this.selectedRowKeys.length > 0;
    },
  },
  methods: {
    b,
    ...mapActions('analytics', [
      'getFrontendErrors',
      'getFrontendErrorDetail',
      'resolveFrontendErrors',
      'clearFrontendErrorDetail',
    ]),
    formatDate(date) {
      if (!date) return '-';
      return dayjs(date).format('MMM DD, YYYY HH:mm:ss');
    },
    getErrorTypeColor(type) {
      const colors = {
        TypeError: 'red',
        ReferenceError: 'orange',
        SyntaxError: 'volcano',
        Error: 'magenta',
        UnhandledRejection: 'purple',
        ConsoleError: 'blue',
      };
      return colors[type] || 'default';
    },
    loadData() {
      this.getFrontendErrors(this.filterParams);
    },
    onFilterChange() {
      this.pagination.page = 1;
      this.selectedRowKeys = [];
      this.loadData();
    },
    onPeriodChange() {
      if (this.filters.period !== 'custom') {
        this.filters.dateRange = [];
        this.onFilterChange();
      }
    },
    onDateRangeChange() {
      if (this.filters.dateRange.length === 2) {
        this.onFilterChange();
      }
    },
    onSearch() {
      this.onFilterChange();
    },
    onTableChange(pagination) {
      this.pagination.page = pagination.current;
      this.pagination.limit = pagination.pageSize;
      this.loadData();
    },
    async viewErrorDetail(error) {
      this.selectedError = error;
      this.errorDetailVisible = true;
      await this.getFrontendErrorDetail(error.id);
    },
    closeErrorDetail() {
      this.errorDetailVisible = false;
      this.selectedError = null;
      this.clearFrontendErrorDetail();
    },
    async resolveSelectedErrors() {
      if (!this.hasSelected) return;
      await this.resolveFrontendErrors({ ids: this.selectedRowKeys });
      this.selectedRowKeys = [];
      this.loadData();
    },
    async resolveError(errorId) {
      await this.resolveFrontendErrors({ ids: [errorId] });
      this.loadData();
      if (this.errorDetailVisible && this.selectedError?.id === errorId) {
        await this.getFrontendErrorDetail(errorId);
      }
    },
    viewSession(sessionId) {
      if (!sessionId) return;
      this.$router.push(`/analytics/sessions/${sessionId}`);
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>

<template>
  <div :class="b()">
    <page-header :title="title">
      <template #actions>
        <a-space>
          <a-button
            v-if="hasSelected"
            type="primary"
            :loading="resolvingErrors"
            @click="resolveSelectedErrors"
          >
            <check-circle-outlined />
            Resolve Selected ({{ selectedRowKeys.length }})
          </a-button>
          <a-button @click="loadData">
            <reload-outlined />
            Refresh
          </a-button>
        </a-space>
      </template>
    </page-header>

    <!-- Filters -->
    <a-card :class="b('filters')">
      <a-row :gutter="[16, 16]">
        <a-col :xs="24" :sm="12" :md="4">
          <a-select
            v-model:value="filters.period"
            :options="periodOptions"
            placeholder="Period"
            style="width: 100%"
            @change="onPeriodChange"
          />
        </a-col>
        <a-col v-if="filters.period === 'custom'" :xs="24" :sm="12" :md="5">
          <a-range-picker
            v-model:value="filters.dateRange"
            :show-time="false"
            style="width: 100%"
            @change="onDateRangeChange"
          />
        </a-col>
        <a-col :xs="24" :sm="12" :md="4">
          <a-select
            v-model:value="filters.errorType"
            :options="errorTypeOptions"
            placeholder="Error Type"
            style="width: 100%"
            @change="onFilterChange"
          />
        </a-col>
        <a-col :xs="24" :sm="12" :md="4">
          <a-select
            v-model:value="filters.isResolved"
            :options="resolvedOptions"
            placeholder="Status"
            style="width: 100%"
            @change="onFilterChange"
          />
        </a-col>
        <a-col :xs="24" :sm="12" :md="6">
          <a-input-search
            v-model:value="filters.search"
            placeholder="Search by message or page"
            @search="onSearch"
          />
        </a-col>
      </a-row>
    </a-card>

    <!-- Errors Table -->
    <a-card :class="b('table-card')">
      <a-table
        :dataSource="errors"
        :columns="columns"
        :loading="errorsLoading"
        :pagination="paginationConfig"
        :row-selection="rowSelection"
        :scroll="{ x: 1000 }"
        row-key="id"
        @change="onTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'error'">
            <div :class="b('error-cell')">
              <a-tag :color="getErrorTypeColor(record.errorType)" size="small">
                {{ record.errorType }}
              </a-tag>
              <div :class="b('error-message')">{{ record.errorMessage }}</div>
              <div v-if="record.sourceFile" :class="b('error-source')">
                {{ record.sourceFile }}:{{ record.lineNumber }}
              </div>
            </div>
          </template>

          <template v-else-if="column.key === 'occurredAt'">
            {{ formatDate(record.occurredAt) }}
          </template>

          <template v-else-if="column.key === 'isResolved'">
            <a-tag v-if="record.isResolved" color="green">
              <check-circle-outlined /> Resolved
            </a-tag>
            <a-tag v-else color="red">
              <exclamation-circle-outlined /> Unresolved
            </a-tag>
          </template>

          <template v-else-if="column.key === 'actions'">
            <a-space>
              <a-button type="link" size="small" @click="viewErrorDetail(record)">
                <eye-outlined /> Details
              </a-button>
              <a-button
                v-if="record.sessionId"
                type="link"
                size="small"
                @click="viewSession(record.sessionId)"
              >
                <play-circle-outlined /> Replay
              </a-button>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- Error Detail Drawer -->
    <a-drawer
      v-model:visible="errorDetailVisible"
      title="Error Details"
      width="600"
      :body-style="{ padding: '16px' }"
      @close="closeErrorDetail"
    >
      <a-spin :spinning="errorDetailLoading">
        <template v-if="errorDetail">
          <a-descriptions :column="1" bordered size="small">
            <a-descriptions-item label="Error Type">
              <a-tag :color="getErrorTypeColor(errorDetail.errorType)">
                {{ errorDetail.errorType }}
              </a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="Message">
              {{ errorDetail.errorMessage }}
            </a-descriptions-item>
            <a-descriptions-item label="Page URL">
              {{ errorDetail.pageUrl }}
            </a-descriptions-item>
            <a-descriptions-item label="Source File">
              {{ errorDetail.sourceFile || '-' }}
            </a-descriptions-item>
            <a-descriptions-item label="Line/Column">
              {{ errorDetail.lineNumber || '-' }}:{{ errorDetail.columnNumber || '-' }}
            </a-descriptions-item>
            <a-descriptions-item label="Browser">
              {{ errorDetail.browser }} / {{ errorDetail.os }}
            </a-descriptions-item>
            <a-descriptions-item label="Device">
              {{ errorDetail.deviceType }}
            </a-descriptions-item>
            <a-descriptions-item label="User">
              {{ errorDetail.userEmail || 'Anonymous' }}
            </a-descriptions-item>
            <a-descriptions-item label="Occurred">
              {{ formatDate(errorDetail.occurredAt) }}
            </a-descriptions-item>
            <a-descriptions-item label="Status">
              <a-tag v-if="errorDetail.isResolved" color="green">Resolved</a-tag>
              <a-tag v-else color="red">Unresolved</a-tag>
              <span v-if="errorDetail.resolvedAt" style="margin-left: 8px; color: #999">
                {{ formatDate(errorDetail.resolvedAt) }}
              </span>
            </a-descriptions-item>
          </a-descriptions>

          <a-divider>Stack Trace</a-divider>
          <pre
            v-if="errorDetail.stackTrace"
            :class="b('stack-trace')"
          >{{ errorDetail.stackTrace }}</pre>
          <a-empty v-else description="No stack trace available" />

          <a-divider />
          <a-space>
            <a-button
              v-if="!errorDetail.isResolved"
              type="primary"
              :loading="resolvingErrors"
              @click="resolveError(errorDetail.id)"
            >
              <check-circle-outlined /> Mark as Resolved
            </a-button>
            <a-button
              v-if="errorDetail.sessionId"
              @click="viewSession(errorDetail.sessionId)"
            >
              <play-circle-outlined /> View Session Replay
            </a-button>
          </a-space>
        </template>
      </a-spin>
    </a-drawer>
  </div>
</template>

<style lang="scss">
.frontend-errors {
  padding: 24px;

  &__filters {
    margin-bottom: 16px;
  }

  &__error-cell {
    max-width: 400px;
  }

  &__error-message {
    margin-top: 4px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__error-source {
    font-size: 12px;
    color: #999;
    font-family: monospace;
  }

  &__stack-trace {
    background: #f5f5f5;
    padding: 12px;
    border-radius: 4px;
    font-size: 12px;
    font-family: monospace;
    overflow-x: auto;
    white-space: pre-wrap;
    word-break: break-all;
    max-height: 300px;
    overflow-y: auto;
  }
}
</style>
